import { useState } from 'react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../../foundation/altzero_base/base/components/ui/collapsible';
import type { TouBand, TouByNmi, TouWindowOut } from '../types/nemTypes';

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Format a decimal hour (7.5) as 'HH:MM'. */
function fmtTime(decimal: number): string {
  const h = Math.floor(decimal) % 24;
  const m = Math.round((decimal - Math.floor(decimal)) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** [0,1,2,3,4] -> "Mon-Fri"; scattered -> "Mon, Tue, Thu". */
function fmtWeekdays(days: number[]): string {
  if (days.length === 0) return '—';
  const sorted = [...days].sort((a, b) => a - b);
  // Contiguous run?
  const contiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (contiguous && sorted.length > 1) {
    return `${WEEKDAY_NAMES[sorted[0]]}-${WEEKDAY_NAMES[sorted[sorted.length - 1]]}`;
  }
  return sorted.map((d) => WEEKDAY_NAMES[d]).join(', ');
}

function WindowRows({ label, windows }: { label: string; windows: TouWindowOut[] }) {
  if (windows.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        <span className="font-medium text-gray-600">{label}:</span> none
      </p>
    );
  }
  return (
    <div className="text-xs text-gray-600">
      <p className="font-medium text-gray-600">{label}</p>
      <ul className="ml-3 list-disc text-gray-500">
        {windows.map((w, i) => (
          <li key={i}>
            {fmtWeekdays(w.weekdays)} {fmtTime(w.start)}–{fmtTime(w.end)}
          </li>
        ))}
      </ul>
    </div>
  );
}

const BAND_COLOUR: Record<TouBand, string> = {
  superpeak: 'text-red-700',
  peak: 'text-rose-600',
  shoulder: 'text-amber-600',
  offpeak: 'text-gray-500',
};

export const TouCalculationCard = ({ entry }: { entry: TouByNmi }) => {
  const [open, setOpen] = useState(false);
  const schedule = entry.schedule;
  const hasSlots = entry.streams.some((s) => s.slots && s.slots.length > 0);

  if (!schedule && !hasSlots) return null; // backward compat: nothing to show

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="w-full px-4 py-2 text-left text-xs font-medium text-gray-500 hover:bg-gray-50 border-t">
        {open ? '▼ How calculated' : '▶ How calculated'}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 py-3 space-y-2 border-t bg-gray-50/50">
          {schedule && (
            <>
              <p className="text-xs text-gray-600">
                <span className="font-medium">Retailer:</span> {entry.retailer} ·{' '}
                <span className="font-medium">State:</span> {entry.state} ·{' '}
                <span className="font-medium">Holidays applied:</span>{' '}
                {schedule.holidayNames.length > 0 ? schedule.holidayNames.join(', ') : 'none'}
              </p>
              <WindowRows label="Peak windows" windows={schedule.peak} />
              <WindowRows label="Shoulder windows" windows={schedule.shoulder} />
              {schedule.holidays.length > 0 && (
                <p className="text-xs text-gray-500">
                  <span className="font-medium text-gray-600">Holiday dates → Off Peak:</span>{' '}
                  {schedule.holidays.join(', ')}
                </p>
              )}
            </>
          )}

          {hasSlots &&
            entry.streams.map((s) =>
              s.slots && s.slots.length > 0 ? (
                <div key={`${s.suffix}-${s.meterSerial}`} className="space-y-1">
                  <p className="text-xs font-medium text-gray-600">
                    {s.suffix} slots ({s.slots.length})
                  </p>
                  <div className="max-h-64 overflow-y-auto rounded border bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="py-1 px-2 text-left font-medium text-gray-500">Date</th>
                          <th className="py-1 px-2 text-left font-medium text-gray-500">Time</th>
                          <th className="py-1 px-2 text-right font-medium text-gray-500">kWh</th>
                          <th className="py-1 px-2 text-left font-medium text-gray-500">Band</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {s.slots.map((slot) => (
                          <tr key={`${slot.date}-${slot.slot}`}>
                            <td className="py-1 px-2 text-gray-600">{slot.date}</td>
                            <td className="py-1 px-2 text-gray-600">{slot.time}</td>
                            <td className="py-1 px-2 text-right text-gray-800">{slot.kwh}</td>
                            <td className={`py-1 px-2 font-medium ${BAND_COLOUR[slot.band]}`}>
                              {slot.band}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null,
            )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
